import { Globe, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { ENABLED_LANGUAGES } from "@/lib/i18n/languages";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  /** Visual variant of the trigger. */
  variant?: "ghost" | "outline";
  className?: string;
}

/**
 * Language picker — replaces the binary ar↔en Globe toggle. Lists every
 * `enabled` language by its endonym so a Chinese investor sees "简体中文", a
 * Japanese one "日本語", etc. Selecting a language lazy-loads its locale chunk
 * (see LanguageProvider.setLanguage) before switching.
 */
export function LanguageSwitcher({ variant = "ghost", className }: LanguageSwitcherProps) {
  const { language, setLanguage, t } = useLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size="icon"
          className={cn("rounded-full", className)}
          aria-label={t("اللغة", "Language")}
        >
          <Globe className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-y-auto bg-popover z-50">
        <DropdownMenuLabel>{t("اللغة", "Language")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ENABLED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className="flex items-center justify-between gap-2"
            dir={lang.dir}
          >
            <span className="flex flex-col">
              <span className="text-sm font-medium">{lang.nativeName}</span>
              {lang.nativeName !== lang.englishName && (
                <span className="text-xs text-muted-foreground">{lang.englishName}</span>
              )}
            </span>
            {language === lang.code && <Check className="h-4 w-4 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
